/**
 * Chat状态管理 Store
 *
 * 使用 zustand 管理AI Chat状态，包括会话、消息、工具调用、思考过程等。
 * 使用 SSE (Server-Sent Events) 进行流式通信。
 *
 * v3.0: 重构为 SSE 流式通信
 * - 移除 WebSocket 依赖，改用 HTTP SSE
 * - 使用 parts-based 消息模型，统一管理流式内容
 * - 简化状态管理，移除碎片化的状态字段
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { chatApi } from '../services/chatApi';
import { chatSessionApi } from '../services/chatSessionApi';
import { useAgentStore } from './agentStore';
import { useBranchStore } from './branchStore';
import type {
  ChatMessage,
  ChatSession,
  ToolCall,
  ChatMessageSend,
  ChatModel,
  ChatSessionCreate,
  ToolEvent,
  SkillEvent,
  TodoItem,
  TodoUpdateEvent,
  QuestionAskedEvent,
  LlmRetryingEvent,
  StreamingMessageState,
  StreamingMessagePart,
  MessagePart,
} from '../types/chat';
import {
  validateMessageContent,
  isValidSessionId,
  isValidChatModel,
  ToolName,
  isToolDetectedEvent,
  isToolExecutingEvent,
  isToolCompletedEvent,
  isSkillActivationEvent,
  isQuestionAskedEvent
} from '../types/chat';
import { sanitizeErrorMessage, validateToken } from '../utils/validation';
import { useRefreshStore } from './refreshStore';
import { settingApi } from '../services/settingApi';
import { useAutoTrackStore, triggerSettingTrack, triggerChapterTrack } from './autoTrackStore';

// ==================== 超时配置 ====================

const TIMEOUT = {
  SHORT: 10000,
  NORMAL: 10000,
  LONG: 180000
} as const;

// ==================== 辅助函数 ====================

const startLoading = (set: (fn: (state: ChatState) => void) => void) => {
  set((state) => {
    state.loading = true;
    state.error = null;
  });
};

const handleError = (
  set: (fn: (state: ChatState) => void) => void,
  error: unknown,
  defaultMessage: string
) => {
  const errorMessage = sanitizeErrorMessage(error, defaultMessage);
  set((state) => {
    state.error = errorMessage;
    state.loading = false;
  });
};

// ==================== Store 状态接口 ====================

interface ChatState {
  // ===== 会话相关 =====
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  currentModel: ChatModel;
  sessionsLoaded: boolean;

  // ===== 消息相关 =====
  messages: ChatMessage[];
  streamingMessage: StreamingMessageState | null;
  isStreaming: boolean;
  abortController: AbortController | null;

  // ===== 技能激活相关 =====
  skillActivation: {
    skillName: string;
    description?: string;
    status: 'activating' | 'active' | 'error';
    error?: string;
  } | null;

  // ===== 子智能体调用相关 =====
  subagentInvocation: {
    subagentName: string;
    task?: string;
    status: 'invoking' | 'working' | 'completed' | 'error';
    result?: string;
    error?: string;
    executionTime?: number;
    streamContent?: string;
    toolCalls?: Array<{
      tool_call_id?: string;
      tool_name: string;
      status: 'detected' | 'executing' | 'success' | 'failed';
      tool_args?: Record<string, unknown>;
      result?: unknown;
      error?: string;
      timestamp: string;
    }>;
    messages?: Array<{
      role: 'assistant' | 'tool';
      content?: string;
      tool_name?: string;
      timestamp: string;
    }>;
  } | null;

  // ===== Todo 任务清单相关 =====
  todos: TodoItem[];

  // ===== 问答相关 =====
  pendingQuestion: QuestionAskedEvent | null;

  // ===== 状态标记 =====
  loading: boolean;
  error: string | null;
  isCleaning: boolean;
  workspaceId: number;
  lastTokensUsed: number;
  _pendingNewSession: boolean;
  /** 当前流式请求关联的会话ID（新会话首条消息时由 SSE 事件回填，用于显式中止） */
  activeStreamSessionId: string | null;

  // ===== 会话管理方法 =====
  fetchSessions: (token: string, workspaceId: number, page?: number, limit?: number) => Promise<void>;
  createSession: (token: string, workspaceId: number, data: ChatSessionCreate) => Promise<ChatSession>;
  loadSession: (token: string, sessionId: string) => Promise<void>;
  switchSession: (token: string, sessionId: string) => Promise<void>;
  setCurrentSession: (session: ChatSession | null) => void;
  deleteSession: (token: string, workspaceId: number, sessionId: string) => Promise<void>;
  updateSessionModel: (token: string, sessionId: string, model: string) => Promise<void>;

  // ===== 消息管理方法 =====
  sendMessage: (token: string, data: ChatMessageSend) => Promise<void>;
  retryMessage: (token: string, messageIndex: number) => Promise<void>;
  abortMessage: (sessionId: string) => void;
  appendMessage: (message: ChatMessage) => void;
  clearMessages: () => void;

  // ===== 流式消息处理方法 =====
  handleStreamEvent: (event: { type: string; data: Record<string, unknown> }) => void;
  finalizeStreamingMessage: (endData: Record<string, unknown>) => void;

  // ===== 工具调用处理方法 =====
  handleToolEvent: (event: ToolEvent) => void;
  updateToolCallStatus: (toolName: string, status: ToolCall['status'], result?: unknown, error?: string) => void;

  // ===== 技能激活处理方法 =====
  handleSkillEvent: (event: SkillEvent) => void;

  // ===== 子智能体调用处理方法 =====
  handleSubagentEvent: (event: unknown, eventType: 'started' | 'updated' | 'completed') => void;

  // ===== Todo 任务清单处理方法 =====
  handleTodoUpdate: (event: TodoUpdateEvent) => void;
  clearTodos: () => void;

  // ===== 问答处理方法 =====
  handleQuestionAsked: (event: QuestionAskedEvent) => void;
  submitQuestionAnswer: (questionId: string, answers: string[][]) => Promise<void>;
  dismissQuestion: (questionId: string) => Promise<void>;

  // ===== LLM 重试处理方法 =====
  handleLlmRetrying: (event: LlmRetryingEvent) => void;

  // ===== 模型管理方法 =====
  setCurrentModel: (model: string) => void;

  // ===== 初始化方法 =====
  initialize: (workspaceId: number) => void;

  // ===== 清理方法 =====
  clearError: () => void;
  cleanup: () => void;
}

// ==================== 创建 Chat Store ====================

export const useChatStore = create<ChatState>()(
  immer((set, get) => ({
  // ===== 初始状态 =====
  sessions: [],
  currentSession: null,
  currentModel: '',
  messages: [],
  streamingMessage: null,
  isStreaming: false,
  abortController: null,
  skillActivation: null,
  subagentInvocation: null,
  todos: [],
  pendingQuestion: null,
  loading: false,
  error: null,
  isCleaning: false,
  sessionsLoaded: false,
  workspaceId: 0,
  lastTokensUsed: 0,
  _pendingNewSession: false,
  activeStreamSessionId: null,

  // ===== 会话管理 =====

  fetchSessions: async (token: string, workspaceId: number, page: number = 1, limit: number = 20) => {
    console.log('[ChatStore] fetchSessions调用 - workspaceId:', workspaceId, 'page:', page);
    validateToken(token);
    if (get().isCleaning) return;

    startLoading(set);
    try {
      const { currentBranch } = useBranchStore.getState();
      const branchId = currentBranch?.id ?? null;

      const response = await chatSessionApi.listSessions(workspaceId, branchId, page, limit);

      console.log('[ChatStore] fetchSessions收到响应 - sessions数量:', response.sessions?.length, 'total:', response.total);

      set((state) => {
        const sessionsData = response.sessions || [];
        console.log('[ChatStore] 准备更新sessions - 当前数量:', state.sessions.length, '新数据数量:', sessionsData.length);
        if (page === 1) {
          state.sessions = sessionsData;
        } else {
          state.sessions = [...state.sessions, ...sessionsData];
        }
        state.loading = false;
        state.sessionsLoaded = true;
        console.log('[ChatStore] sessions更新完成 - 最终数量:', state.sessions.length);
      });
    } catch (error) {
      console.error('[ChatStore] fetchSessions失败:', error);
      handleError(set, error, '获取会话列表失败，请稍后重试');
      set((state) => {
        state.sessionsLoaded = true;
      });
      throw error;
    }
  },

  createSession: async (token: string, workspaceId: number, data: ChatSessionCreate): Promise<ChatSession> => {
    validateToken(token);
    if (get().isCleaning) {
      throw new Error('当前正在清理资源，无法创建会话');
    }

    startLoading(set);
    try {
      const { currentBranch } = useBranchStore.getState();
      const branchId = data.branch_id !== undefined ? data.branch_id : (currentBranch?.id ?? null);

      const newSession = await chatSessionApi.createSession({
        workspace_id: workspaceId,
        title: data.title || '新对话',
        model: data.model,
        branch_id: branchId,
      });

      set((state) => {
        state.sessions.push(newSession);
        state.currentSession = newSession;
        if (newSession.model && isValidChatModel(newSession.model)) {
          state.currentModel = newSession.model;
        }
        state.messages = [];
        state.todos = [];
        state.streamingMessage = null;
        state.abortController = null;
        state.pendingQuestion = null;
        state.loading = false;
      });

      return newSession;
    } catch (error) {
      handleError(set, error, '创建会话失败，请稍后重试');
      throw error;
    }
  },

  setCurrentSession: (session: ChatSession | null) => {
    if (session && !isValidSessionId(session.id)) {
      throw new Error('无效的会话ID');
    }

    set((state) => {
      state.currentSession = session;
      state.messages = [];
      state.streamingMessage = null;
      state.abortController = null;
      state.todos = [];
      state.lastTokensUsed = 0;

      if (session && session.model && isValidChatModel(session.model)) {
        state.currentModel = session.model;
      }
    });
  },

  deleteSession: async (token: string, workspaceId: number, sessionId: string) => {
    validateToken(token);
    if (!isValidSessionId(sessionId)) {
      throw new Error('无效的会话ID');
    }

    console.log('[ChatStore] deleteSession调用 - workspaceId:', workspaceId, 'sessionId:', sessionId);
    startLoading(set);
    try {
      await chatSessionApi.deleteSession(sessionId, workspaceId);
      console.log('[ChatStore] deleteSession成功');

      set((state) => {
        state.sessions = state.sessions.filter((s: ChatSession) => s.id !== sessionId);
        if (state.currentSession?.id === sessionId) {
          state.currentSession = null;
        }
        state.loading = false;
      });
    } catch (error) {
      console.error('[ChatStore] deleteSession失败:', error);
      handleError(set, error, '删除会话失败，请稍后重试');
      throw error;
    }
  },

  loadSession: async (token: string, sessionId: string) => {
    validateToken(token);
    if (!isValidSessionId(sessionId)) {
      throw new Error('无效的会话ID');
    }
    if (get().isCleaning) return;

    startLoading(set);
    try {
      const response = await chatSessionApi.getSession(sessionId, get().workspaceId);

      const processedMessages = (response.messages || []).map(msg => {
        if (msg.type) return msg;
        
        let messageType: ChatMessage['type'] = 'chat';
        if (msg.skill_activation) {
          messageType = 'skill-activation';
        } else if (msg.subagent_invocation) {
          messageType = 'subagent-invocation';
        } else if (msg.tool_calls && msg.tool_calls.length > 0) {
          messageType = 'tool_call';
        } else if (msg.thinking_process) {
          messageType = 'chat.thinking';
        }
        
        return { ...msg, type: messageType };
      });

      set((state) => {
        state.currentSession = response.session;
        state.messages = processedMessages;
        state.lastTokensUsed = response.session?.context_tokens || 0;
        state.todos = [];
        state.streamingMessage = null;
        state.abortController = null;
        state.pendingQuestion = null;
        state.loading = false;
      });

      const workspaceId = get().workspaceId;
      if (workspaceId) {
        localStorage.setItem(`activeSessionId_${workspaceId}`, sessionId);
      }
    } catch (error) {
      handleError(set, error, '加载会话失败，请稍后重试');
      throw error;
    }
  },

  switchSession: async (token: string, sessionId: string) => {
    validateToken(token);
    if (!isValidSessionId(sessionId)) {
      throw new Error('无效的会话ID');
    }
    if (get().isCleaning) return;

    startLoading(set);
    try {
      await get().loadSession(token, sessionId);

      set((state) => {
        state.loading = false;
      });
    } catch (error) {
      handleError(set, error, '切换会话失败，请稍后重试');
      throw error;
    }
  },

  updateSessionModel: async (token: string, sessionId: string, model: string) => {
    validateToken(token);
    if (!isValidSessionId(sessionId)) {
      throw new Error('无效的会话ID');
    }
    if (!isValidChatModel(model)) {
      throw new Error('无效的模型名称');
    }
    if (get().isCleaning) return;

    startLoading(set);
    try {
      const updatedSession = await chatSessionApi.switchModel(
        sessionId,
        get().workspaceId,
        model
      );

      set((state) => {
        state.sessions = state.sessions.map((s: ChatSession) =>
          s.id === sessionId ? { ...s, model } : s
        );
        if (state.currentSession?.id === sessionId) {
          state.currentSession = { ...state.currentSession, model };
          state.currentModel = model;
        }
        state.loading = false;
      });
    } catch (error) {
      handleError(set, error, '更新模型失败，请稍后重试');
      throw error;
    }
  },

  // ===== 消息管理 =====

  sendMessage: async (token: string, data: ChatMessageSend) => {
    validateToken(token);
    if (get().isCleaning) return;

    startLoading(set);
    try {
      if (!validateMessageContent(data.content)) {
        throw new Error('消息内容无效或超过长度限制');
      }

      const timestamp = new Date().toISOString();
      const userMessage: ChatMessage = {
        type: 'chat',
        role: 'user',
        content: data.content,
        timestamp,
        status: 'pending'
      };

      get().appendMessage(userMessage);

      const isNewSession = !data.session_id && !get().currentSession;

      const { currentAgentId } = useAgentStore.getState();
      const { currentBranch } = useBranchStore.getState();
      const branchId = data.branch_id !== undefined ? data.branch_id : (currentBranch?.id ?? null);

      const abortController = new AbortController();
      set((state) => {
        state.isStreaming = true;
        state.streamingMessage = { parts: [], isComplete: false, sequence: 0 };
        state.abortController = abortController;
        state.todos = [];
      });

      set((state) => {
        const msg = state.messages.find(m => m.timestamp === timestamp);
        if (msg) {
          msg.status = 'sent';
        }
      });

      const sessionId = isNewSession ? undefined : (data.session_id || get().currentSession?.id);
      set((state) => {
        state.activeStreamSessionId = sessionId ?? null;
      });
      const request: ChatMessageSend = {
        session_id: sessionId,
        content: data.content,
        model: data.model || get().currentModel,
        stream: true,
        workspace_id: get().workspaceId,
        agent_id: currentAgentId || undefined,
        branch_id: branchId,
      };

      try {
        for await (const event of chatApi.sendMessageStream(sessionId, request, abortController.signal)) {
          get().handleStreamEvent(event);
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          console.log('[ChatStore] 消息已中止');
        } else {
          throw error;
        }
      }
    } catch (error) {
      handleError(set, error, '发送消息失败，请稍后重试');
      set((state) => {
        state.isStreaming = false;
        state.streamingMessage = null;
        state.abortController = null;
        const lastUserMsg = [...state.messages].reverse().find(m => m.role === 'user');
        if (lastUserMsg && lastUserMsg.status === 'pending') {
          lastUserMsg.status = 'fail';
        }
      });
      throw error;
    }
  },

  handleStreamEvent: (event: { type: string; data: Record<string, unknown> }) => {
    const { type, data } = event;

    // 新会话首条消息时，从事件中回填实际会话ID，供中止接口使用
    const eventSessionId = data.session_id as string | undefined;
    if (eventSessionId && eventSessionId !== get().activeStreamSessionId) {
      set((state) => {
        state.activeStreamSessionId = eventSessionId;
      });
    }

    switch (type) {
      case 'chat.stream': {
        const content = (data.content as string) || '';
        set((state) => {
          if (!state.streamingMessage) {
            state.streamingMessage = { parts: [], isComplete: false, sequence: 0 };
          }
          const textParts = state.streamingMessage.parts.filter(p => p.type === 'text');
          const lastTextPart = textParts[textParts.length - 1];
          if (lastTextPart) {
            lastTextPart.content += content;
          } else {
            state.streamingMessage.parts.push({
              type: 'text',
              content,
              sequence: state.streamingMessage.sequence++,
            });
          }
        });
        break;
      }

      case 'chat.thinking': {
        const thinkingProcess = (data.thinking_process as string) || '';
        set((state) => {
          if (!state.streamingMessage) {
            state.streamingMessage = { parts: [], isComplete: false, sequence: 0 };
          }
          const reasoningPart = state.streamingMessage.parts.find(p => p.type === 'reasoning');
          if (reasoningPart) {
            reasoningPart.content = thinkingProcess;
          } else {
            state.streamingMessage.parts.unshift({
              type: 'reasoning',
              content: thinkingProcess,
              sequence: state.streamingMessage.sequence++,
            });
          }
        });
        break;
      }

      case 'tool.detected':
      case 'tool.executing':
      case 'tool.completed': {
        get().handleToolEvent(data as unknown as ToolEvent);
        break;
      }

      case 'chat.end': {
        get().finalizeStreamingMessage(data);
        break;
      }

      case 'skill.activation':
      case 'skill.error': {
        get().handleSkillEvent(data as unknown as SkillEvent);
        break;
      }

      case 'subagent_started': {
        get().handleSubagentEvent(data, 'started');
        break;
      }

      case 'subagent_updated': {
        get().handleSubagentEvent(data, 'updated');
        break;
      }

      case 'subagent_completed': {
        get().handleSubagentEvent(data, 'completed');
        break;
      }

      case 'todo.updated': {
        get().handleTodoUpdate(data as unknown as TodoUpdateEvent);
        break;
      }

      case 'question.asked': {
        if (isQuestionAskedEvent(data)) {
          get().handleQuestionAsked(data);
        }
        break;
      }

      case 'llm.retrying': {
        get().handleLlmRetrying(data as unknown as LlmRetryingEvent);
        break;
      }

      case 'error': {
        const errorMessage = (data.error as string) || '未知错误';
        console.error('[ChatStore] SSE error:', errorMessage);
        set((state) => {
          state.error = errorMessage;
          state.isStreaming = false;
          state.streamingMessage = null;
          state.abortController = null;
          state.loading = false;
        });
        break;
      }
    }
  },

  finalizeStreamingMessage: (endData: Record<string, unknown>) => {
    const sessionId = endData.session_id as string | undefined;
    const messageId = endData.message_id as number | undefined;
    const isFinal = endData.is_final !== false;
    const step = endData.step as number | undefined;
    const toolCalls = (endData.tool_calls as ToolCall[]) || [];
    const thinkingProcess = endData.thinking_process as string | undefined;
    const contextTokensTotal = endData.context_tokens_total as number | undefined;

    set((state) => {
      if (contextTokensTotal !== undefined) {
        state.lastTokensUsed = contextTokensTotal;
      }

      const streamingMessage = state.streamingMessage;
      let content = '';
      
      if (streamingMessage) {
        const textPart = streamingMessage.parts.find(p => p.type === 'text');
        content = textPart?.content || '';
      }

      if (messageId && messageId > 0) {
        const existingIndex = state.messages.findIndex(m => m.id === messageId);
        if (existingIndex >= 0) {
          state.messages[existingIndex] = {
            ...state.messages[existingIndex],
            content,
            tool_calls: toolCalls,
            thinking_process: thinkingProcess,
          };
          if (isFinal) {
            state.isStreaming = false;
            state.streamingMessage = null;
            state.abortController = null;
            state.loading = false;
          } else {
            state.streamingMessage = { parts: [], isComplete: false, sequence: 0 };
          }
          return;
        }
      }

      const convertStreamingPartsToMessageParts = (
        streamingParts: StreamingMessagePart[] | undefined
      ): MessagePart[] | undefined => {
        if (!streamingParts || streamingParts.length === 0) return undefined;
        const sorted = [...streamingParts].sort((a, b) => a.sequence - b.sequence);
        return sorted.map((part, index) => {
          if (part.type === 'tool') {
            return {
              type: 'tool_call',
              sequence: index,
              content: part.content,
              tool_call_id: part.tool_call_id,
              tool_name: part.tool_name,
              status: part.status,
              tool_args: part.tool_args,
              result: part.result,
              error: part.error,
            };
          }
          return {
            type: part.type,
            sequence: index,
            content: part.content,
          };
        });
      };

      const assistantMessage: ChatMessage = {
        id: messageId,
        type: 'chat',
        role: 'assistant',
        content,
        parts: convertStreamingPartsToMessageParts(streamingMessage?.parts),
        thinking_process: thinkingProcess,
        timestamp: new Date().toISOString(),
        tool_calls: toolCalls,
        step,
      };

      state.messages.push(assistantMessage);

      if (isFinal) {
        state.isStreaming = false;
        state.streamingMessage = null;
        state.abortController = null;
        state.loading = false;

        if (state._pendingNewSession && sessionId) {
          state._pendingNewSession = false;
          if (!state.currentSession || state.currentSession.id !== sessionId) {
            const now = new Date().toISOString();
            state.currentSession = {
              id: sessionId,
              workspace_id: state.workspaceId,
              title: '新对话',
              model: state.currentModel,
              created_at: now,
              updated_at: now,
            };
          }
        }

        setTimeout(() => {
          const token = localStorage.getItem('token');
          if (token) {
            get().fetchSessions(token, state.workspaceId, 1, 20).catch(console.error);
          }
        }, 100);
      } else {
        state.streamingMessage = { parts: [], isComplete: false, sequence: 0 };
      }
    });
  },

  appendMessage: (message: ChatMessage) => {
    set((state) => {
      state.messages.push(message);
    });
  },

  clearMessages: () => {
    set((state) => {
      state.messages = [];
      state.streamingMessage = null;
    });
  },

  // ===== 消息重试 =====
  retryMessage: async (token: string, messageIndex: number) => {
    validateToken(token);
    if (get().isCleaning) return;

    const { messages, currentSession, currentModel } = get();
    const failedMessage = messages[messageIndex];

    if (!failedMessage || failedMessage.status !== 'fail') {
      console.warn('[ChatStore] 只能重试发送失败的消息');
      return;
    }

    // 更新消息状态为pending
    set((state) => {
      state.messages[messageIndex].status = 'pending';
      state.error = null;
    });

    try {
      await get().sendMessage(token, {
        session_id: currentSession?.id,
        content: failedMessage.content,
        model: currentModel,
        stream: true,
        workspace_id: get().workspaceId
      });

      set((state) => {
        state.messages = state.messages.filter((_, idx) => idx !== messageIndex);
      });
    } catch (error) {
      set((state) => {
        state.messages[messageIndex].status = 'fail';
      });
      throw error;
    }
  },

  abortMessage: (sessionId: string) => {
    console.log('[ChatStore] 中止消息', { sessionId });
    
    const abortController = get().abortController;
    if (abortController) {
      abortController.abort();
    }

    // 显式通知后端中止 agent：经 nginx 代理时客户端断连可能不传播到后端，
    // 仅 abort fetch 无法可靠停止 agent，必须调用后端中止接口
    const targetSessionId = get().activeStreamSessionId || sessionId;
    if (targetSessionId) {
      chatSessionApi.abortSessionMessage(targetSessionId).catch((err) => {
        console.warn('[ChatStore] 后端中止请求失败:', err);
      });
    }

    set((state) => {
      state.isStreaming = false;
      state.streamingMessage = null;
      state.abortController = null;
      state.activeStreamSessionId = null;
      state.loading = false;
    });
  },

  // ===== 工具事件处理 =====

  handleToolEvent: (event: ToolEvent) => {
    console.log('[ChatStore] handleToolEvent 触发 - type:', event.type, 'tool_name:', event.tool_name);

    if (event.tool_name === 'ask_user' as ToolName) {
      console.log('[ChatStore] 跳过 ask_user 工具事件，由 question.asked 事件处理');
      return;
    }

    set((state) => {
      if (!state.streamingMessage) {
        state.streamingMessage = { parts: [], isComplete: false, sequence: 0 };
      }

      const parts = state.streamingMessage.parts;
      const seq = state.streamingMessage.sequence;
      const existingIndex = parts.findIndex(
        (p) => p.type === 'tool' && p.tool_call_id === event.tool_call_id
      );

      if (isToolDetectedEvent(event)) {
        const toolPart: StreamingMessagePart = {
          type: 'tool',
          content: '',
          tool_call_id: event.tool_call_id,
          tool_name: event.tool_name,
          status: 'detected',
          tool_args: event.tool_args,
          sequence: 0,
        };

        if (existingIndex >= 0) {
          toolPart.sequence = parts[existingIndex].sequence;
          parts[existingIndex] = toolPart;
        } else {
          const lastTextIdx = [...parts].reverse().findIndex(p => p.type === 'text');
          if (lastTextIdx >= 0) {
            const actualIdx = parts.length - 1 - lastTextIdx;
            const textPart = parts[actualIdx];

            const textBefore: StreamingMessagePart = {
              type: 'text',
              content: textPart.content,
              sequence: seq,
            };
            toolPart.sequence = seq + 1;

            parts.splice(actualIdx, 1, textBefore, toolPart);
            state.streamingMessage.sequence = seq + 2;
          } else {
            toolPart.sequence = seq;
            parts.push(toolPart);
            state.streamingMessage.sequence = seq + 1;
          }
        }
        console.log('[ChatStore] 工具检测到:', event.tool_name);
      } else if (isToolExecutingEvent(event)) {
        if (existingIndex >= 0) {
          parts[existingIndex].status = 'executing';
        } else {
          parts.push({
            type: 'tool',
            content: '',
            tool_call_id: event.tool_call_id,
            tool_name: event.tool_name,
            status: 'executing',
            tool_args: event.tool_args,
            sequence: seq,
          });
          state.streamingMessage.sequence = seq + 1;
        }
        console.log('[ChatStore] 工具执行中:', event.tool_name);
      } else if (isToolCompletedEvent(event)) {
        if (existingIndex >= 0) {
          parts[existingIndex].status = event.status;
          if (event.result !== undefined) {
            parts[existingIndex].result = event.result;
          }
          if (event.error !== undefined) {
            parts[existingIndex].error = event.error;
          }
        } else {
          parts.push({
            type: 'tool',
            content: '',
            tool_call_id: event.tool_call_id,
            tool_name: event.tool_name,
            status: event.status,
            tool_args: event.tool_args,
            result: event.result,
            error: event.error,
            sequence: seq,
          });
          state.streamingMessage.sequence = seq + 1;
        }
        console.log('[ChatStore] 工具完成:', event.tool_name, 'status:', event.status);

        if (event.status === 'success' && event.result) {
          const result = event.result as { id?: number; updated?: number[] };
          switch (event.tool_name) {
            case ToolName.SETTING_WRITE:
              useRefreshStore.getState().triggerSettingsRefresh(result.id);
              if (result.id && useAutoTrackStore.getState().enabled) {
                triggerSettingTrack(result.id);
              }
              break;
            case ToolName.CHAPTER_WRITE:
              useRefreshStore.getState().triggerChaptersRefresh(result.id);
              if (result.id && useAutoTrackStore.getState().enabled) {
                triggerChapterTrack(result.id);
              }
              break;
            case ToolName.CHAPTER_UPDATE:
              useRefreshStore.getState().triggerChaptersRefresh(result.id);
              if (result.id && useAutoTrackStore.getState().enabled) {
                const updateResult = result as {
                  id?: number;
                  highlight_start?: number;
                  highlight_end?: number;
                };
                triggerChapterTrack(result.id, {
                  startOffset: updateResult.highlight_start,
                  endOffset: updateResult.highlight_end,
                });
              }
              break;
            case ToolName.TITLE_RENAME:
              useRefreshStore.getState().triggerChaptersRefresh();
              break;
          }
        }
      }
    });
  },

  updateToolCallStatus: (toolName: string, status: ToolCall['status'], result?: unknown, error?: string) => {
    if (!toolName) {
      console.warn('[ChatStore] 无效的工具名称');
      return;
    }

    set((state) => {
      if (!state.streamingMessage) return;
      const toolPart = state.streamingMessage.parts.find(
        (p) => p.type === 'tool' && p.tool_name === toolName
      );
      if (!toolPart) {
        console.warn(`[ChatStore] 未找到工具调用: ${toolName}`);
        return;
      }

      toolPart.status = status;
      if (result !== undefined) {
        toolPart.result = typeof result === 'object' ? JSON.parse(JSON.stringify(result)) : result;
      }
      if (error !== undefined) {
        toolPart.error = sanitizeErrorMessage(new Error(error), '工具执行失败');
      }
    });
  },

  // ===== 技能激活处理 =====

  handleSkillEvent: (event: SkillEvent) => {
    console.log('[ChatStore] 处理技能事件 - type:', event.type, 'skill_name:', (event as any).skill_name);

    set((state) => {
      if (isSkillActivationEvent(event)) {
        state.skillActivation = {
          skillName: event.skill_name,
          description: event.description,
          status: event.status,
          error: event.error
        };
        console.log('[ChatStore] 技能激活:', event.skill_name, 'status:', event.status);

        // 如果状态为 active，自动清除（模拟卡片自动消失）
        if (event.status === 'active') {
          setTimeout(() => {
            set((s) => {
              if (s.skillActivation?.skillName === event.skill_name) {
                s.skillActivation = null;
              }
            });
          }, 3000);
        }
      } else {
        // 技能错误事件
        state.skillActivation = {
          skillName: (event as any).skill_name,
          status: 'error',
          error: (event as any).error
        };
      }
    });
  },

  // ===== 子智能体调用处理 =====

  handleSubagentEvent: (event: unknown, eventType: 'started' | 'updated' | 'completed') => {
    const data = event as Record<string, any>;
    console.log('[ChatStore] 处理子智能体事件 - eventType:', eventType, 'data:', data);

    set((state) => {
      if (eventType === 'started') {
        const subagentName = data.agent_name || data.subagent_name || 'Unknown';
        const task = data.task || `调用 ${subagentName} 处理任务`;

        // 固化当前的 streamingMessage
        if (state.streamingMessage && state.streamingMessage.parts.length > 0) {
          const textPart = state.streamingMessage.parts.find(p => p.type === 'text');
          if (textPart && textPart.content) {
            const segmentMessage: ChatMessage = {
              type: 'chat',
              role: 'assistant',
              content: textPart.content,
              timestamp: new Date().toISOString(),
            };
            state.messages.push(segmentMessage);
            console.log('[ChatStore] 子智能体启动 - 固化streamingMessage');
          }
          state.streamingMessage = { parts: [], isComplete: false, sequence: 0 };
        }

        state.subagentInvocation = {
          subagentName: subagentName,
          task: task,
          status: 'working',
          result: undefined,
          error: undefined,
          executionTime: undefined,
          streamContent: '',
          toolCalls: [],
          messages: [],
        };
        console.log('[ChatStore] 子智能体启动 - 更新状态:', subagentName);
        
        const subagentSessionId = data.session_id;
        if (subagentSessionId) {
          useAgentStore.getState().updateSubagentSession(subagentSessionId, {
            sessionId: subagentSessionId,
            parentSessionId: data.parent_session_id || state.currentSession?.id,
            agentId: data.agent_id,
            agentName: subagentName,
            status: 'running',
            startedAt: data.timestamp || new Date().toISOString(),
            streamContent: '',
            toolCalls: [],
            messages: [],
          });
        }
        
        // 3. 在消息列表中创建 subagent_invocation 消息
        // 检查是否已存在相同 subagent_session_id 的消息（优先使用唯一ID）
        const existingMessage = subagentSessionId
          ? state.messages.find(m => m.subagent_invocation?.subagent_session_id === subagentSessionId)
          : state.messages.find(m => m.subagent_invocation?.subagent_name === subagentName);
        
        if (!existingMessage) {
          const newMessage: ChatMessage = {
            type: 'subagent-invocation',
            role: 'assistant',
            content: `🔍 调用子智能体 **${subagentName}** 协助处理任务`,
            subagent_invocation: {
              subagent_name: subagentName,
              task: task,
              status: 'working',
              subagent_session_id: subagentSessionId,
            },
            timestamp: data.timestamp || new Date().toISOString(),
          };
          state.messages.push(newMessage);
          console.log('[ChatStore] 子智能体启动 - 创建消息:', subagentName);
        }
      } else if (eventType === 'updated' || eventType === 'completed') {
        // subagent_updated 或 subagent_completed 事件
        const subagentName = data.agent_name || data.subagent_name;
        if (!subagentName) {
          console.warn('[ChatStore] 子智能体事件缺少 agent_name 字段');
          return;
        }

        const status = data.status === 'completed' ? 'completed' : 
                       data.status === 'failed' || data.status === 'timeout' ? 'error' : 'working';

        // 1. 更新 subagentInvocation 状态
        if (state.subagentInvocation?.subagentName === subagentName) {
          // 更新流式内容
          if (data.result !== undefined && state.subagentInvocation) {
            state.subagentInvocation.streamContent = data.result;
          }
          
          // 更新工具调用事件
          if (data.tool_event && state.subagentInvocation) {
            const toolEvent = data.tool_event;
            const existingToolIndex = state.subagentInvocation.toolCalls?.findIndex(
              t => t.tool_call_id === toolEvent.tool_call_id
            ) ?? -1;
            
            if (existingToolIndex >= 0 && state.subagentInvocation.toolCalls) {
              // 更新现有工具调用
              state.subagentInvocation.toolCalls[existingToolIndex] = {
                ...state.subagentInvocation.toolCalls[existingToolIndex],
                ...toolEvent,
              };
            } else if (state.subagentInvocation.toolCalls) {
              // 添加新工具调用
              state.subagentInvocation.toolCalls.push(toolEvent);
            }
            
            console.log('[ChatStore] 子智能体工具事件:', toolEvent.tool_name, toolEvent.status);
          }
          
          // 更新状态和其他字段
          if (state.subagentInvocation) {
            state.subagentInvocation.status = status;
            if (data.result !== undefined && status === 'completed') {
              state.subagentInvocation.result = data.result;
            }
            if (data.error !== undefined) {
              state.subagentInvocation.error = data.error;
            }
            if (data.duration !== undefined) {
              state.subagentInvocation.executionTime = data.duration;
            }
          }
          console.log('[ChatStore] 子智能体状态更新 -', subagentName, 'status:', status);
        }

        // 3. 同步更新 agentStore（用于 SubagentSessionCards 显示）
        const subagentSessionId = data.session_id;
        if (subagentSessionId) {
          const agentStoreUpdate: {
            status: 'running' | 'completed' | 'failed' | 'timeout' | 'pending';
            streamContent?: string;
            toolCalls?: Array<{
              tool_call_id?: string;
              tool_name: string;
              status: 'detected' | 'executing' | 'success' | 'failed';
              tool_args?: Record<string, unknown>;
              result?: unknown;
              error?: string;
              timestamp: string;
            }>;
            result?: string;
            duration?: number;
            completedAt?: string;
            error?: string;
          } = {
            status: status === 'completed' ? 'completed' : status === 'error' ? 'failed' : 'running',
          };
          
          if (data.result !== undefined) {
            agentStoreUpdate.streamContent = data.result;
          }
          
          if (data.tool_event) {
            const toolEvent = data.tool_event;
            const currentSession = useAgentStore.getState().getSubagentSession(subagentSessionId);
            const existingToolCalls = currentSession?.toolCalls || [];
            
            const existingIndex = existingToolCalls.findIndex(
              t => t.tool_call_id === toolEvent.tool_call_id
            );
            
            if (existingIndex >= 0) {
              existingToolCalls[existingIndex] = {
                ...existingToolCalls[existingIndex],
                ...toolEvent,
              };
            } else {
              existingToolCalls.push(toolEvent);
            }
            
            agentStoreUpdate.toolCalls = existingToolCalls;
          }
          
          if (status === 'completed') {
            agentStoreUpdate.result = data.result;
            agentStoreUpdate.duration = data.duration;
            agentStoreUpdate.completedAt = data.timestamp || new Date().toISOString();
          } else if (status === 'error') {
            agentStoreUpdate.error = data.error;
            agentStoreUpdate.duration = data.duration;
            agentStoreUpdate.completedAt = data.timestamp || new Date().toISOString();
          }
          
          useAgentStore.getState().updateSubagentSession(subagentSessionId, agentStoreUpdate);
          console.log('[ChatStore] 子智能体状态同步到 agentStore:', subagentName, status);
        }

        // 4. 更新消息列表中的对应消息（由后端创建的 subagent invocation 消息）
        // 优先使用 subagent_session_id 查找，避免跨会话误匹配
        const existingIndex = subagentSessionId
          ? state.messages.findIndex(m => m.subagent_invocation?.subagent_session_id === subagentSessionId)
          : state.messages.findIndex(m => m.subagent_invocation?.subagent_name === subagentName);

        if (existingIndex >= 0) {
          state.messages[existingIndex].subagent_invocation!.status = status;
          
          if (data.result !== undefined) {
            state.messages[existingIndex].subagent_invocation!.result = data.result;
          }
          if (data.error !== undefined) {
            state.messages[existingIndex].subagent_invocation!.error = data.error;
          }
          if (data.duration !== undefined) {
            state.messages[existingIndex].subagent_invocation!.execution_time = data.duration;
          }

          console.log('[ChatStore] 子智能体状态更新 - 已更新消息:', subagentName, '新状态:', status);
        } else {
          // 如果找不到对应的消息，可能是消息还未加载或已经被过滤
          // 尝试通过 parent_session_id 匹配当前会话
          const parentSessionId = data.parent_session_id;
          const currentSessionId = state.currentSession?.id;
          
          if (parentSessionId === currentSessionId) {
            console.log('[ChatStore] 子智能体状态更新 - 在当前会话中未找到消息，等待消息加载:', subagentName);
          } else {
            console.log('[ChatStore] 子智能体状态更新 - 事件属于其他会话，跳过:', subagentName);
          }
        }
      }
    });
  },

  // ===== Todo 任务清单处理 =====

  handleTodoUpdate: (event: TodoUpdateEvent | {todos: TodoItem[]}) => {
    // 处理两种格式：完整事件对象 或 只有 data 部分
    const todos = 'data' in event ? event.data.todos : event.todos;
    console.log('[ChatStore] 处理 Todo 更新事件 - todos count:', todos?.length);

    set((state) => {
      state.todos = todos || [];
    });
  },

  clearTodos: () => {
    set((state) => {
      state.todos = [];
    });
  },

  // ===== 问答处理 =====

  handleQuestionAsked: (event: QuestionAskedEvent) => {
    console.log('[ChatStore] 处理提问事件 - question_id:', event.question_id, 'questions:', event.questions?.length);

    set((state) => {
      state.pendingQuestion = event;
    });
  },

  // ===== LLM 重试处理 =====

  handleLlmRetrying: (event: LlmRetryingEvent) => {
    console.log('[ChatStore] LLM 空响应重试 - retry_count:', event.retry_count, 'max_retries:', event.max_retries);

    // 可以在这里添加前端重试提示逻辑，比如显示一个临时提示
    // 目前先记录日志，让用户知道系统正在自动重试
  },

  submitQuestionAnswer: async (questionId: string, answers: string[][]) => {
    console.log('[ChatStore] 提交问答答案 - questionId:', questionId, 'answers:', answers);

    try {
      await chatSessionApi.replyQuestion(questionId, answers);

      set((state) => {
        state.pendingQuestion = null;
      });

      console.log('[ChatStore] 答案提交成功');
    } catch (error) {
      console.error('[ChatStore] 提交答案失败:', error);
      throw error;
    }
  },

  dismissQuestion: async (questionId: string) => {
    console.log('[ChatStore] 取消问题 - questionId:', questionId);

    try {
      await chatSessionApi.dismissQuestion(questionId);

      set((state) => {
        state.pendingQuestion = null;
      });

      console.log('[ChatStore] 问题已取消');
    } catch (error) {
      console.error('[ChatStore] 取消问题失败:', error);
      throw error;
    }
  },

  setCurrentModel: (model: ChatModel) => {
    set((state) => {
      state.currentModel = model;
    });
  },

  // ===== 初始化 =====

  initialize: (workspaceId: number) => {
    console.log('[ChatStore] initialize 被调用 - workspaceId:', workspaceId);

    set((state) => {
      state.sessions = [];
      state.currentSession = null;
      state.messages = [];
      state.sessionsLoaded = false;
      state.workspaceId = workspaceId;
    });

    const storageKey = `activeSessionId_${workspaceId}`;
    localStorage.removeItem(storageKey);

    settingApi.getDefaultModel()
      .then(defaultModel => {
        set((state) => {
          state.currentModel = defaultModel;
        });
        console.log('[ChatStore] 默认模型已设置:', defaultModel);
      })
      .catch(err => {
        console.error('[ChatStore] 获取默认模型失败:', err);
      });

    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[ChatStore] 未找到 token，跳过会话加载');
      return;
    }

    const activeSessionId = localStorage.getItem(storageKey);
    if (activeSessionId) {
      get().loadSession(token, activeSessionId).catch(async () => {
        console.warn('[ChatStore] Failed to restore active session');
        localStorage.removeItem(storageKey);

        try {
          await get().fetchSessions(token, workspaceId, 1, 20);
          const sessions = get().sessions;
          if (sessions.length > 0) {
            await get().loadSession(token, sessions[0].id);
            localStorage.setItem(storageKey, sessions[0].id);
          }
        } catch (fallbackError) {
          console.error('[ChatStore] Fallback failed:', fallbackError);
        }
      });
    } else {
      get().fetchSessions(token, workspaceId, 1, 20).then(async () => {
        const sessions = get().sessions;
        if (sessions.length > 0) {
          await get().loadSession(token, sessions[0].id);
          localStorage.setItem(storageKey, sessions[0].id);
        }
      }).catch((error) => {
        console.error('[ChatStore] Failed to load sessions:', error);
      });
    }
  },

  clearError: () => {
    set((state) => {
      state.error = null;
    });
  },

  cleanup: () => {
    set((state) => {
      state.isCleaning = true;
      state.loading = false;
      state.isStreaming = false;
      state.abortController?.abort();
      state.abortController = null;
    });

    set((state) => {
      state.sessions = [];
      state.currentSession = null;
      state.messages = [];
      state.streamingMessage = null;
      state.todos = [];
      state.pendingQuestion = null;
      state.loading = false;
      state.error = null;
      state.isCleaning = false;
      state.sessionsLoaded = false;
    });
  }
})));

export default useChatStore;
